import { NavLink, Outlet } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Store, Package, Tags, ShoppingBag, Crown, Users, Globe, Coins } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Loading } from "@/components/RequireAuth";
import { StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useState } from "react";

const NAV = [
  ["/admin", "Aperçu", LayoutDashboard, "overview"], ["/admin/boutiques", "Vendeurs & boutiques", Store, "shops"],
  ["/admin/produits", "Produits", Package, "products"], ["/admin/categories", "Catégories", Tags, "categories"],
  ["/admin/commandes", "Commandes", ShoppingBag, "orders"], ["/admin/abonnements", "Paiements et abonnements", Crown, "subscriptions"],
  ["/admin/utilisateurs", "Clients & comptes", Users, "users"], ["/admin/pays", "Pays", Globe, "countries"], ["/admin/taux", "Taux de change", Coins, "rates"],
];

export default function AdminLayout() {
  return (
    <div data-testid="admin-layout" className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 md:grid-cols-[230px_1fr] md:px-6">
      <aside className="min-w-0 md:sticky md:top-24 md:h-fit">
        <p className="mb-3 rounded-xl bg-gray-950 p-4 font-display text-lg font-bold text-white">Administration</p>
        <nav className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto px-4 md:mx-0 md:flex-col md:px-0">
          {NAV.map(([to, label, I, id]) => (
            <NavLink key={to} to={to} end={to === "/admin"} data-testid={`admin-nav-${id}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100"}`}>
              <I className="h-4 w-4" />{label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <section className="min-w-0"><Outlet /></section>
    </div>
  );
}

export function AdminOverview() {
  const { data } = useApi("/admin/stats");
  if (!data) return <Loading />;
  const items = [["Comptes", data.users], ["Demandes vendeurs", data.pending_shops], ["Boutiques approuvées", data.approved_shops], ["Boutiques suspendues", data.suspended_shops], ["Produits", data.products], ["Commandes", data.orders], ["Abonnements en attente", data.pending_subscriptions], ["Abonnements actifs", data.active_subscriptions], ["Fiches IA générées", data.ai_jobs]];
  return (
    <div data-testid="admin-overview" className="space-y-5">
      <h1 className="font-display text-3xl font-black tracking-tight">Vue d'ensemble</h1>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {items.map(([l, v]) => <div key={l} className="rounded-xl border bg-white p-4"><p className="tnum font-display text-3xl font-black">{v}</p><p className="text-xs text-gray-500">{l}</p></div>)}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [q, setQ] = useState("");
  const { data, reload } = useApi(`/admin/users?q=${encodeURIComponent(q)}`);
  const verifyEmail = async (u) => {
    if (!window.confirm(`Confirmer que l'adresse ${u.email} a été vérifiée ?`)) return;
    try { await api.post(`/admin/users/${u.id}/verify-email`); toast.success("E-mail validé"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resetLink = async (u) => {
    if (!window.confirm(`Avez-vous vérifié l'identité de ${u.name} (${u.email}) ? Le lien permet de changer son mot de passe.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-link`);
      window.prompt(`Lien à usage unique, valable ${data.expires_in_minutes} minutes. Copiez-le et transmettez-le uniquement à cette personne :`, `${window.location.origin}${data.link}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (u) => {
    try { await api.put(`/admin/users/${u.id}/status`, { status: u.status === "suspended" ? "active" : "suspended" }); toast.success("Compte mis à jour"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-users" className="space-y-5">
      <h1 className="font-display text-3xl font-black tracking-tight">Clients & comptes</h1>
      <Input data-testid="admin-users-search" placeholder="Rechercher par nom ou e-mail" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
      {!data ? <Loading /> : (
        <div className="divide-y rounded-xl border bg-white">
          {data.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.email}`} className="flex flex-wrap items-center gap-3 p-3 text-sm">
              <div className="min-w-0 flex-1"><p className="font-semibold">{u.name} {u.demo && <span className="text-xs text-indigo-600">(démo)</span>}</p><p className="truncate text-gray-500">{u.email} · {u.role} · inscrit le {fmtDate(u.created_at)}</p></div>
              <StatusBadge status={u.status === "suspended" ? "suspended" : "active"} kind="shop" />
              {u.role !== "admin" && !u.email_verified && <Button size="sm" variant="outline" onClick={() => verifyEmail(u)} data-testid={`admin-user-verify-${u.email}`}>Valider l'e-mail</Button>}
              {u.role !== "admin" && <Button size="sm" variant="outline" onClick={() => resetLink(u)} data-testid={`admin-user-reset-${u.email}`}>Lien de réinitialisation</Button>}
              {u.role !== "admin" && <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`admin-user-toggle-${u.email}`}>{u.status === "suspended" ? "Réactiver" : "Suspendre"}</Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminCountries() {
  const { data, reload } = useApi("/countries");
  if (!data) return <Loading />;
  const opened = data.filter((c) => c.selling_enabled);
  return (
    <div data-testid="admin-countries" className="space-y-5">
      <h1 className="font-display text-3xl font-black tracking-tight">Pays</h1>
      <p data-testid="countries-open-summary" className="text-sm text-gray-700"><b>{opened.length}</b> pays ouvert(s) à la vente : {opened.map((c) => c.name).join(", ") || "aucun"}. Les autres restent fermés jusqu'à votre décision. Fermer un pays ne supprime rien : ses boutiques sont masquées et ses commandes bloquées jusqu'à la réouverture.</p>
      <div data-testid="country-opening-checklist" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-950 ring-1 ring-amber-200">
        <p className="font-bold">Avant d'ouvrir un pays, préparez :</p>
        <ul className="mt-1 list-disc pl-5">
          <li>le taux de change de sa monnaie (Admin › Taux de change) ;</li>
          <li>un moyen de paiement réellement utilisable (abonnements en FCFA, paiement à la livraison) ;</li>
          <li>une solution de livraison fiable (zones et frais définis par les vendeurs).</li>
        </ul>
        <p className="mt-2 text-xs">Pour ouvrir un pays : activez « Vente », puis « Livraison » et, si souhaité, « Paiement à la livraison » sur sa ligne ci-dessous. Sans « Vente », aucun abonnement ne peut être demandé ni activé dans ce pays.</p>
      </div>
      <p className="text-sm text-gray-500">Chaque pays a sa monnaie officielle. Wave, Orange Money et les autres paiements en ligne ne peuvent pas être activés tant que leur intégration n'est pas configurée.</p>
      <div className="divide-y rounded-xl border bg-white">
        {[...opened, ...data.filter((c) => !c.selling_enabled)].map((c) => <CountryRow key={c.code} c={c} reload={reload} />)}
      </div>
    </div>
  );
}

const CountryRow = ({ c, reload }) => {
  const cod = !!c.payment_methods?.find((m) => m.code === "paiement_livraison")?.enabled;
  const save = async (patch) => {
    try {
      await api.put(`/admin/countries/${c.code}`, { selling_enabled: !!c.selling_enabled, delivery_enabled: !!c.delivery_enabled, cash_on_delivery: cod, ...patch });
      toast.success(`${c.name} mis à jour`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const Toggle = ({ label, checked, k }) => (
    <label className="flex items-center gap-2 text-xs"><Switch data-testid={`country-${c.code}-${k}`} checked={checked} onCheckedChange={(v) => save({ [k]: v })} />{label}</label>
  );
  return (
    <div data-testid={`admin-country-${c.code}`} className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4 text-sm">
      <span className="min-w-[220px] flex-1 font-semibold">{c.name} ({c.code}) · {c.currency_label} ({c.currency}){c.decimals ? ` · ${c.decimals} déc.` : ""}</span>
      <Toggle label="Vente" checked={!!c.selling_enabled} k="selling_enabled" />
      <Toggle label="Livraison" checked={!!c.delivery_enabled} k="delivery_enabled" />
      <Toggle label="Paiement à la livraison" checked={cod} k="cash_on_delivery" />
      <span className="w-full text-xs text-gray-500">Paiements en ligne : {c.payment_methods?.filter((m) => m.code !== "paiement_livraison").map((m) => `${m.name} (non configuré)`).join(", ") || "aucun prévu pour l'instant"}</span>
    </div>
  );
};
